import {inject, Injectable, NgZone} from '@angular/core';
import {FirestoreService} from './firestore.service';
import {RedirectService} from './redirect.service';
import {Router} from '@angular/router';
import {Auth, onAuthStateChanged} from '@angular/fire/auth';
import {MaintenanceByPlatformModel, MaintenanceInfoModel, MaintenanceViewModel, VersionInfoApiModel, VersionInfoModel} from '@core/models';
import {ModalController, Platform} from '@ionic/angular';
import {App} from '@capacitor/app';
import {Capacitor} from '@capacitor/core';
import {AppUpdateDialogComponent} from '@shared/components';
import {BehaviorSubject, combineLatest, defer, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Collection} from 'spotcharge-types';
import {environment} from '../../../environments/environment';

const ALLOWED_DEEP_LINK_HOSTS = new Set([
  'book.spotcharge.co.in',
  'spotcharge-book-dev.web.app',
]);

const DEFAULT_MAINTENANCE_TITLE = 'Under maintenance';
const DEFAULT_MAINTENANCE_MESSAGE = 'SpotCharge is temporarily unavailable. Please try again shortly.';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  readonly maintenance$ = new BehaviorSubject<MaintenanceViewModel | null>(null);
  private modal?: HTMLIonModalElement;
  private router = inject(Router);
  private auth = inject(Auth);
  private redirectService = inject(RedirectService);
  private zone = inject(NgZone);

  constructor(
    private firestoreService: FirestoreService,
    private platform: Platform,
    private modalCtrl: ModalController,
  ) {
    // Listen for appUrlOpen events (deep links)
    App.addListener('appUrlOpen', (data: any) => {
      this.zone.run(() => {
        this.handleDeepLink(data.url);
      });
    });

    // Handle cold-start deep links (when app is opened from a killed state)
    void App.getLaunchUrl().then((launchData) => {
      if (launchData?.url) {
        this.zone.run(() => {
          this.handleDeepLink(launchData.url);
        });
      }
    });

    console.log(this.platform.platforms());
  }

  private async handleDeepLink(url: string): Promise<void> {
    try {
      const parsedUrl = new URL(url);
      const host = parsedUrl.host.toLowerCase();
      if (!ALLOWED_DEEP_LINK_HOSTS.has(host)) {
        return;
      }

      const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      
      if (!path || path === '/') {
        return;
      }

      console.log('Deep link received:', url, 'Path:', path);

      // Wait for auth state to be ready (handles Firebase Auth initialization delay)
      const user = await this.waitForAuthState();
      
      if (user) {
        // User is logged in, navigate directly
        await this.router.navigateByUrl(path, {replaceUrl: true});
      } else {
        // User is not logged in, store URL and redirect to login
        await this.redirectService.setRedirectUrl(path);
        await this.router.navigate(['/login'], {replaceUrl: true});
      }
    } catch (e) {
      console.error('Error handling deep link:', e);
    }
  }

  private waitForAuthState(): Promise<any> {
    return new Promise((resolve) => {
      // Always use onAuthStateChanged to wait for Firebase Auth to initialize
      // This ensures we get the correct auth state even if Firebase is still loading
      const unsubscribe = onAuthStateChanged(this.auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  async getAppVersion() {
    if (this.platform.is('capacitor')) {
      const appInfo = await App.getInfo();
      return appInfo.version;
    } else {
      return environment.appVersion;
    }
  }

  getServerVersion(): Observable<VersionInfoApiModel> {
    return this.firestoreService.doc$(`${Collection.versionInfo}/version`) as Observable<VersionInfoApiModel>;
  }

  checkAppVersion() {
    return combineLatest([this.getServerVersion(), defer(() => this.getAppVersion())]).pipe(
      map(([appVersionData, currentVersion]) => {
        const maintenance = this.toMaintenanceView(this.maintenanceForPlatform(appVersionData?.maintenance));
        this.maintenance$.next(maintenance);
        if (maintenance) {
          return;
        }
        if (!appVersionData) {
          return;
        }
        let updateInfo: VersionInfoModel;
        if (this.platform.is('android')) {
          updateInfo = appVersionData.androidPublic;
        } else if (this.platform.is('ios')) {
          updateInfo = appVersionData.iosPublic;
        } else {
          updateInfo = appVersionData.androidPublic;
        }
        console.log('updateInfo', updateInfo);
        console.log('!updateInfo.isEnabled', !updateInfo.isEnabled);
        if (!updateInfo.isEnabled) {
          if (this.modal) {
            this.modal.dismiss().then();
          }
          return;
        }

        // Compare the current version to the latest version
        const currentVersionArr = currentVersion.split('.');
        const latestVersionArr = updateInfo.currentVersion.split('.');
        console.log(currentVersionArr, latestVersionArr);
        const isUpdatedNeeded = this.checkForUpdate(currentVersionArr, latestVersionArr);
        console.log('isUpdatedNeeded', isUpdatedNeeded);

        if (isUpdatedNeeded && updateInfo.forceUpdate) {
          // Display a dialog asking the user to update the app and provide the appropriate update info
          this.showUpdateModal(updateInfo).then();
        } else {
          if (this.modal) {
            this.modal.dismiss().then();
          }
        }
      })
    );
  }

  private maintenanceForPlatform(maintenance?: MaintenanceByPlatformModel): MaintenanceInfoModel | undefined {
    if (!maintenance) {
      return undefined;
    }
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      return maintenance.android;
    }
    if (platform === 'ios') {
      return maintenance.ios;
    }
    return maintenance.web;
  }

  private toMaintenanceView(maintenance?: MaintenanceInfoModel): MaintenanceViewModel | null {
    if (!maintenance?.enabled) {
      return null;
    }
    return {
      title: maintenance.title?.trim() || DEFAULT_MAINTENANCE_TITLE,
      message: maintenance.message?.trim() || DEFAULT_MAINTENANCE_MESSAGE,
    };
  }

  checkForUpdate(currentVersionArr: string[], latestVersionArr: string[]): boolean {
    for (let i = 0; i < 3; i++) {
      const storedPart = Number(latestVersionArr[i]);
      const currentPart = Number(currentVersionArr[i]);
      if (storedPart > currentPart) {
        return true;
      } else if (storedPart < currentPart) {
        return false;
      }
    }
    return false;
  }

  async showUpdateModal(updateInfo: VersionInfoModel) {
    this.modal = await this.modalCtrl.create({
      component: AppUpdateDialogComponent,
      componentProps: {
        updateInfo
      },
      cssClass: ['custom-dialog-wrapper'],
      // showBackdrop: true,
      backdropDismiss: !updateInfo.forceUpdate
    });
    await this.modal.present();
  }
}
